import React, { useState } from 'react';
import styled from 'styled-components';
import { MdRocket, MdDiamond, MdMovie, MdCardGiftcard, MdCelebration } from 'react-icons/md';
import type { IconType } from 'react-icons';

const SubscribeContainer = styled.section`
  padding: 5rem 2rem 4rem;
  background: 
    radial-gradient(circle at 20% 30%, rgba(212, 175, 55, 0.015) 0%, transparent 50%),
    radial-gradient(circle at 80% 70%, rgba(212, 175, 55, 0.012) 0%, transparent 50%),
    radial-gradient(circle at 40% 10%, rgba(212, 175, 55, 0.008) 0%, transparent 60%),
    radial-gradient(circle at 60% 90%, rgba(212, 175, 55, 0.006) 0%, transparent 60%),
    linear-gradient(135deg, var(--color-charcoal) 0%, var(--color-obsidian) 25%, var(--color-charcoal) 50%, var(--color-obsidian) 75%, var(--color-charcoal) 100%);
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: 
      radial-gradient(circle at 25% 25%, rgba(212, 175, 55, 0.012) 0%, transparent 60%),
      radial-gradient(circle at 75% 75%, rgba(212, 175, 55, 0.008) 0%, transparent 60%),
      radial-gradient(circle at 50% 50%, rgba(212, 175, 55, 0.004) 0%, transparent 70%);
    pointer-events: none;
    z-index: 1;
    animation: luxury-ambient-glow 35s ease-in-out infinite;
  }
  
  &::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 600px;
    height: 600px;
    background: radial-gradient(circle at center, rgba(212, 175, 55, 0.004) 0%, rgba(212, 175, 55, 0.002) 40%, transparent 70%);
    border-radius: 50%;
    filter: blur(12rem);
    pointer-events: none;
    z-index: 1;
    animation: luxury-pulse 18s ease-in-out infinite;
  }
  
  @media (max-width: 968px) {
    padding: 4rem 1.5rem 3rem;
    
    &::after {
      width: 300px;
      height: 300px;
    }
  }
  
  @media (max-width: 768px) {
    padding: 3rem 1rem 2.5rem;
    
    &::after {
      width: 250px;
      height: 250px;
    }
  }
  
  @media (max-width: 576px) {
    padding: 2.5rem 0.75rem 2rem;
    
    &::after {
      width: 200px;
      height: 200px;
    }
  }
  
  @media (max-width: 480px) {
    padding: 2rem 0.5rem 1.75rem;
    
    &::after {
      width: 150px;
      height: 150px;
    }
  }
  
  @media (max-width: 375px) {
    padding: 1.75rem 0.375rem 1.5rem;
    
    &::after {
      width: 120px;
      height: 120px;
    }
  }
  
  @media (max-width: 320px) {
    padding: 1.5rem 0.25rem 1.25rem;
    
    &::after {
      width: 100px;
      height: 100px;
    }
  }
`;

const SubscribeContent = styled.div`
  max-width: 1000px;
  margin: 0 auto;
  text-align: center;
  position: relative;
  z-index: 2;
  
  &::before {
    content: '';
    position: absolute;
    top: -3rem;
    left: -3rem;
    right: -3rem;
    bottom: -3rem;
    background: 
      radial-gradient(circle at 30% 30%, rgba(212, 175, 55, 0.008) 0%, transparent 70%),
      radial-gradient(circle at 70% 70%, rgba(212, 175, 55, 0.006) 0%, transparent 70%);
    border-radius: 24px;
    filter: blur(2rem);
    z-index: -1;
    opacity: 0.9;
    animation: luxury-content-glow 20s ease-in-out infinite;
  }
  
  @media (max-width: 968px) {
    max-width: 90%;
  }
  
  @media (max-width: 768px) {
    max-width: 95%;
  }
`;

const SectionTitle = styled.h2`
  font-size: clamp(2rem, 4vw, 3rem);
  margin-bottom: 1.5rem;
  color: var(--color-pearl);
  font-weight: 950;
  letter-spacing: -0.04em;
  line-height: 1.1;
  position: relative;
  font-family: 'Inter', sans-serif;
  text-shadow: 
    0 4px 8px rgba(11, 11, 11, 0.9),
    0 0 40px rgba(212, 175, 55, 0.12),
    0 0 80px rgba(212, 175, 55, 0.06);
  animation: luxury-glow 8s ease-in-out infinite;
  
  &::before {
    content: '';
    position: absolute;
    top: -20px;
    left: -20px;
    right: -20px;
    bottom: -20px;
    background: 
      radial-gradient(circle at 30% 30%, rgba(212, 175, 55, 0.004) 0%, transparent 70%),
      radial-gradient(circle at 70% 70%, rgba(212, 175, 55, 0.003) 0%, transparent 70%);
    filter: blur(2rem);
    z-index: -1;
    animation: luxury-text-aura 12s ease-in-out infinite;
  }
  
  span {
    background: linear-gradient(135deg, #D4AF37 0%, #F8F8F8 20%, #E8B86D 40%, #F8F8F8 60%, #D4AF37 80%, #E8B86D 100%);
    background-size: 400% 100%;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }
  
  &::after {
    content: '';
    position: absolute;
    bottom: -1rem;
    left: 50%;
    transform: translateX(-50%);
    width: 100px;
    height: 2px;
    background: linear-gradient(90deg, transparent, var(--color-champagne), transparent);
    opacity: 0.6;
  }
`;

const SectionDescription = styled.p`
  font-size: clamp(1rem, 2vw, 1.125rem);
  color: var(--color-slate-300);
  line-height: 1.5;
  margin-bottom: 2rem;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
  font-weight: 400;
  opacity: 0.95;
  letter-spacing: 0.01em;
`;

const SubscriptionForm = styled.form`
  background: rgba(212, 175, 55, 0.03);
  border: 1px solid rgba(212, 175, 55, 0.1);
  border-radius: 10px;
  padding: 2rem 1.5rem;
  margin-bottom: 2rem;
  backdrop-filter: blur(16px);
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(135deg, rgba(212, 175, 55, 0.02) 0%, transparent 50%, rgba(212, 175, 55, 0.01) 100%);
    border-radius: 12px;
    pointer-events: none;
  }
`;

const FormTitle = styled.h3`
  font-size: clamp(1.125rem, 2.5vw, 1.375rem);
  color: var(--color-pearl);
  margin-bottom: 1.5rem;
  font-weight: 600;
  letter-spacing: -0.01em;
  text-shadow: 0 1px 2px rgba(11, 11, 11, 0.8);
  
  span {
    background: linear-gradient(135deg, #D4AF37 0%, #E8B86D 50%, #D4AF37 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }
`;

const InputGroup = styled.div`
  display: flex;
  gap: 0.75rem;
  max-width: 400px;
  margin: 0 auto;
  
  @media (max-width: 600px) {
    flex-direction: column;
  }
`;

const EmailInput = styled.input`
  flex: 1;
  padding: 0.875rem 1.25rem;
  background: rgba(212, 175, 55, 0.025);
  border: 1px solid rgba(212, 175, 55, 0.15);
  border-radius: 6px;
  color: var(--color-pearl);
  font-size: 0.95rem;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  backdrop-filter: blur(12px);
  font-weight: 400;
  letter-spacing: 0.01em;
  
  &:focus {
    outline: none;
    border-color: rgba(212, 175, 55, 0.4);
    box-shadow: 0 0 0 2px rgba(212, 175, 55, 0.08);
    background: rgba(212, 175, 55, 0.04);
    transform: translateY(-1px);
  }
  
  &::placeholder {
    color: var(--color-slate-400);
    font-weight: 300;
    opacity: 0.8;
  }
`;

const SubscribeButton = styled.button`
  background: var(--color-champagne);
  color: var(--color-obsidian);
  padding: 0.875rem 1.75rem;
  border: 1px solid var(--color-champagne);
  border-radius: 6px;
  font-weight: 700;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  white-space: nowrap;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  position: relative;
  overflow: hidden;
  backdrop-filter: blur(8px);
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(254, 254, 254, 0.2), transparent);
    transition: left 0.6s ease;
  }
  
  &:hover::before {
    left: 100%;
  }
  
  &:hover {
    background: var(--color-obsidian);
    color: var(--color-champagne);
    transform: translateY(-3px);
    box-shadow: 0 16px 32px rgba(212, 175, 55, 0.2);
  }
  
  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
    transform: none;
  }
`;

const BenefitsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 2rem;
  margin-top: 3rem;
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 1.75rem;
  }
  
  @media (max-width: 576px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
    margin-top: 2.5rem;
  }
  
  @media (max-width: 480px) {
    gap: 1.25rem;
    margin-top: 2rem;
  }
  
  @media (max-width: 375px) {
    gap: 1rem;
    margin-top: 1.75rem;
  }
  
  @media (max-width: 320px) {
    gap: 0.75rem;
    margin-top: 1.5rem;
  }
`;

const BenefitCard = styled.div`
  background: var(--background-card);
  border: 1px solid var(--border-secondary);
  border-radius: 12px;
  padding: 2rem;
  text-align: center;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  
  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 12px 30px var(--shadow-secondary);
    border-color: var(--border-primary);
  }
  
  @media (max-width: 768px) {
    padding: 1.75rem;
    border-radius: 10px;
  }
  
  @media (max-width: 576px) {
    padding: 1.5rem;
    border-radius: 8px;
  }
  
  @media (max-width: 480px) {
    padding: 1.25rem;
    border-radius: 8px;
  }
  
  @media (max-width: 375px) {
    padding: 1rem;
    border-radius: 6px;
  }
  
  @media (max-width: 320px) {
    padding: 0.875rem;
    border-radius: 6px;
  }
`;

const BenefitIcon = styled.div`
  font-size: 2.2rem;
  margin-bottom: 1rem;
  opacity: 0.8;
`;

const BenefitTitle = styled.h4`
  font-size: 1.05rem;
  color: var(--text-primary);
  margin-bottom: 0.5rem;
  font-weight: 600;
  letter-spacing: -0.01em;
`;

const BenefitDescription = styled.p`
  color: var(--text-muted);
  font-size: 0.9rem;
  line-height: 1.5;
  font-weight: 400;
`;

const SuccessMessage = styled.div`
  background: #059669;
  color: white;
  padding: 1rem 2rem;
  border-radius: 8px;
  margin-top: 1rem;
  font-weight: 500;
`;

interface Benefit {
  icon: IconType;
  title: string;
  description: string;
}

const benefits: Benefit[] = [
  {
    icon: MdRocket,
    title: "Early Access",
    description: "Be the first to know about new releases and get priority access to limited editions"
  },
  {
    icon: MdDiamond,
    title: "Exclusive Drops",
    description: "Access to subscriber-only collectibles and special edition variants"
  },
  {
    icon: MdMovie,
    title: "Behind Scenes",
    description: "Exclusive content showing our crafting process and artist interviews"
  },
  {
    icon: MdCardGiftcard,
    title: "Special Offers",
    description: "Subscriber discounts, bundle deals, and exclusive collector perks"
  }
];

const Subscribe: React.FC = () => {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;

    setIsSubmitting(true);
    
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    setIsSubmitting(false);
    setIsSubscribed(true);
    setEmail('');
    
    // Reset success message after 5 seconds
    setTimeout(() => setIsSubscribed(false), 5000);
  };

  return (
    <SubscribeContainer id="subscribe">
      <SubscribeContent>
        <SectionTitle>JOIN THE <span>WAITLIST</span></SectionTitle>
        <SectionDescription>
          Be the first to know about our upcoming releases, exclusive collector drops, and behind-the-scenes content. 
          Join thousands of collectors who trust KIII Toys Studios for the finest limited-edition collectibles.
        </SectionDescription>
        
        <SubscriptionForm onSubmit={handleSubmit}>
          <FormTitle>Subscribe to Our <span>Exclusive Newsletter</span></FormTitle>
          <InputGroup>
            <EmailInput
              type="email"
              placeholder="Enter your email address"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              disabled={isSubmitting}
            />
            <SubscribeButton type="submit" disabled={isSubmitting || !email}>
              {isSubmitting ? 'Subscribing...' : 'Subscribe'}
            </SubscribeButton>
          </InputGroup>
          {isSubscribed && (
            <SuccessMessage>
              {React.createElement(MdCelebration as React.ComponentType<any>, { style: { marginRight: '0.5rem', fontSize: '1.2rem' } })}
              Welcome to the KIII Toys Studios family! Check your email for exclusive content.
            </SuccessMessage>
          )}
        </SubscriptionForm>
        
        <BenefitsGrid>
          {benefits.map((benefit, index) => (
            <BenefitCard key={index}>
              <BenefitIcon>{React.createElement(benefit.icon as React.ComponentType<any>)}</BenefitIcon>
              <BenefitTitle>{benefit.title}</BenefitTitle>
              <BenefitDescription>{benefit.description}</BenefitDescription>
            </BenefitCard>
          ))}
        </BenefitsGrid>
      </SubscribeContent>
    </SubscribeContainer>
  );
};

export default Subscribe;